// A widget's menu on a phone, from its ⋯ button or a finger held on it: a sheet with what a phone does with a
// widget (full screen, refresh, ask about it, delete). Arranging it on the canvas is for a computer.

import { Maximize2, MessageCircle, RefreshCw, Trash2, TriangleAlert, Wand2 } from "lucide-react";
import type { WidgetItem } from "../../shared/types";
import { SPEC } from "../../shared/catalog";
import { hasPages, kindOf } from "../../shared/boardKinds";
import { htmlHash, problems } from "../../shared/widgetKit";
import { closeContextMenu, useMenu } from "../chrome/ContextMenu";
import { get, set, toast, useStore } from "../state/store";
import { deleteItems } from "../state/actions";
import { runPull } from "../state/sources";
import { removeFromPage } from "../boards/doc/place";
import { askLabel, askWho, builderNow, handOff } from "../agent/handoff";
import { changeAsk, drawAsk } from "../agent/asks";
import { fixIt, fixLabel } from "../widgets/custom";
import { readName } from "../widgets/WidgetTile";
import { Sheet, SheetRow } from "./Sheet";
import { openFull } from "./state";
import { arrange, type MenuEntry } from "../chrome/menus/MenuList";
import { askName } from "../../shared/askWords";

export function PhoneMenu() {
  const open = useMenu((s) => s.open && s.kind === "item");
  const id = useMenu((s) => s.ids[0]);
  const w = useStore((s) => (open && id ? s.items[id] : undefined));
  const title = useStore((s) => (w?.kind === "widget" ? (readName(w, w.stream ? (s.streams[w.stream] ?? null) : null, w.stream ? s.pulls[s.streams[w.stream]?.pullId ?? ""] : null) ?? (w.title || SPEC[w.type]?.label || "Widget")) : ""));
  if (!open || !w || w.kind !== "widget") return null;
  const run = (fn: () => void) => () => {
    closeContextMenu();
    fn();
  };
  return (
    <Sheet label={`${title}: widget menu`} onClose={closeContextMenu} title={<b>{title}</b>}>
      <section className="psheet-group">
        {/* the one order every menu keeps: open it, ask about it, its data, then Delete */}
        {arrange(entries(w), true)
          .filter((e): e is MenuEntry => typeof e !== "string")
          .map((e) => (
            <SheetRow key={e.label} icon={e.icon} label={e.label} danger={e.danger} disabled={e.disabled} onClick={run(e.run)} />
          ))}
      </section>
    </Sheet>
  );
}

type Entry = MenuEntry;

function entries(w: WidgetItem): Entry[] {
  const s = get();
  const spec = SPEC[w.type];
  const board = s.boards.find((b) => b.id === w.boardId);
  const out: Entry[] = [{ label: "Full screen", group: "main", icon: <Maximize2 size={18} />, run: () => openFull(w.id) }];
  const pullId = spec?.reads && w.stream ? s.streams[w.stream]?.pullId : null;
  if (pullId)
    out.push({
      label: "Refresh now",
      group: "data",
      icon: <RefreshCw size={18} />,
      run: () =>
        void runPull(pullId).then(
          (r) => toast({ level: r.pull.status === "ok" ? "success" : "warn", title: r.pull.status === "ok" ? "Refreshed" : "Could not refresh", body: r.pull.error }),
          (e) => toast({ level: "error", title: "Could not refresh", body: (e as Error).message }),
        ),
    });
  const b = builderNow();
  if (w.type === "custom") {
    const html = String(w.props.html ?? "");
    const r = s.renders[w.id];
    const report = r && html && r.hash === htmlHash(html) ? r.report : null;
    if (report && problems(report).length) out.push({ label: fixLabel(b), group: "ask", icon: <TriangleAlert size={18} />, run: () => fixIt(w, report) });
    out.push({
      label: html ? `Change it with ${askWho(b)}` : `Describe it to ${askWho(b)}`,
      group: "ask",
      icon: <Wand2 size={18} />,
      run: () => {
        set({ selection: [w.id] });
        handOff(html ? changeAsk(w) : drawAsk(w));
      },
    });
  } else
    out.push({
      label: `${askLabel(b)} about this`,
      group: "ask",
      icon: <MessageCircle size={18} />,
      run: () => {
        set({ selection: [w.id] });
        handOff({ chat: "What does this tell me, and what should I do about it?", agent: `On my Context Board, what does the widget “${askName(w.title) ?? w.type}” (id ${w.id}) tell me, and what should I do about it?` });
      },
    });
  // in a page, its block goes with it; anywhere else it goes to the trash, with Undo
  out.push({ label: "Delete", group: "delete", icon: <Trash2 size={18} />, danger: true, disabled: !!w.locked, run: () => (hasPages(kindOf(board)) ? removeFromPage(w.id) : deleteItems([w.id])) });
  return out;
}
